import { ReactNode, useMemo } from 'react';

import AlertIcon from '@/assets/icons/alert.svg';
import AlertWarningIcon from '@/assets/icons/alert_warning.svg';
import AlertSuccessIcon from '@/assets/icons/alert_success.svg';
import ApprovalCircleIcon from '@/assets/icons/dashboard/approval-warning.svg';
import ApprovalTriangleIcon from '@/assets/icons/dashboard/approval-triangle.svg';

import './styles.css';

type Variant = 'error' | 'warning' | 'info' | 'success' | 'infoV2' | 'approval' | 'approvalBanner';

// Border treatment for a variant: even 1px border, left-accent bar + 1px border, or left-accent bar only.
type BorderMode = 'full' | 'left' | 'leftAccent';

interface IProps {
    children?: ReactNode;
    label: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    boldLabel?: boolean;
    biggerLabel?: boolean;
    className?: string;
    labelClassName?: string;
    descriptionClassName?: string;
    childrenClassName?: string;
    iconClassName?: string;
    testId?: string;
    variant?: Variant;
    icon?: string;
    iconColor?: string;
    withIcon?: boolean;
    avoidAlertDetection?: boolean;
    // Stack icon / text / action vertically on mobile (row layout from `sm` up).
    stackOnMobile?: boolean;
    // Override the variant's default border treatment / radius when needed.
    borderMode?: BorderMode;
    rounded?: string;
    // Backwards-compatible border aliases (prefer `borderMode`).
    fullBorder?: boolean;
    removeBorder?: boolean;
}

interface Style {
    bg: string;
    border: string;
    text: string;
    icon?: string;
    children?: string;
    // Design baked into the variant so consumers don't have to repeat it.
    iconClassName?: string;
    labelClassName?: string;
    descriptionClassName?: string;
    rounded?: string;
    borderMode?: BorderMode;
    shadow?: string;
}

const APPROVAL_COLORS = { bg: 'bg-[#FFEBB8]', border: 'border-[#F68D13]' };
const APPROVAL_TEXT = {
    text: 'text-dark',
    labelClassName: 'text-base leading-tight lg:text-lg',
    descriptionClassName: 'pt-1 text-15'
};

const styles: { [key in Variant]: Style } = {
    error: {
        bg: 'bg-red-300',
        border: 'border-red-500',
        icon: AlertIcon,
        text: 'text-red-500',
        children: 'rounded-10 bg-alertErrorChildren p-4'
    },
    warning: {
        bg: 'bg-yellow-100',
        border: 'border-yellow-300',
        icon: AlertWarningIcon,
        text: 'text-dark'
    },
    info: {
        bg: 'bg-mint-100',
        border: 'border-mint-400',
        text: 'text-primary-400'
    },
    infoV2: {
        bg: 'bg-primary-1300',
        border: 'border-primary',
        text: 'text-black',
        icon: AlertSuccessIcon
    },
    success: {
        bg: 'bg-mint-200',
        border: 'border-primary-700',
        text: 'text-black',
        icon: AlertSuccessIcon,
        children: 'px-5'
    },
    // Page-level "Your next Monthly Shipment is ready" notice: even border, circle icon, soft shadow.
    approval: {
        ...APPROVAL_COLORS,
        ...APPROVAL_TEXT,
        icon: ApprovalCircleIcon,
        iconClassName: 'h-10 w-10 shrink-0',
        rounded: 'rounded-[15px]',
        borderMode: 'full',
        shadow: 'shadow-medicationAttention'
    },
    // In-block variant of the same notice: orange left-accent bar, triangle icon, no shadow (sits inside a card).
    approvalBanner: {
        ...APPROVAL_COLORS,
        ...APPROVAL_TEXT,
        icon: ApprovalTriangleIcon,
        iconClassName: 'h-6 w-6 shrink-0 self-center',
        rounded: 'rounded-[15px]',
        borderMode: 'leftAccent'
    }
};

const borderClasses = (mode: BorderMode) => {
    switch (mode) {
        case 'full':
            return 'border-1';
        case 'leftAccent':
            return 'border-1 border-l-[5px] border-r-0 border-b-0 border-t-0';
        case 'left':
        default:
            return 'border-1 border-l-[5px]';
    }
};

export const Alert = ({
    children,
    label,
    description,
    action,
    boldLabel = true,
    className = '',
    biggerLabel = false,
    labelClassName = '',
    descriptionClassName = '',
    childrenClassName = '',
    iconClassName,
    variant = 'error',
    testId = 'alert',
    icon,
    iconColor,
    withIcon = true,
    avoidAlertDetection = false,
    stackOnMobile = false,
    borderMode,
    rounded,
    fullBorder,
    removeBorder
}: IProps) => {
    const style = useMemo(() => styles[variant], [variant]);

    // Effective design values: explicit prop → variant's baked-in value → hard default.
    const effectiveRounded = rounded ?? style.rounded ?? 'rounded-lg';
    const effectiveIconClassName = iconClassName ?? style.iconClassName ?? 'h-5 w-5';
    const aliasBorderMode: BorderMode | undefined = fullBorder ? 'full' : removeBorder ? 'leftAccent' : undefined;
    const effectiveBorder = borderClasses(borderMode ?? aliasBorderMode ?? style.borderMode ?? 'left');
    const resolvedIcon = icon || style.icon;

    return (
        <div
            className={`${avoidAlertDetection ? '' : `alert__${variant}`} ${effectiveBorder} ${style.border} relative flex w-full flex-col gap-2 overflow-hidden ${effectiveRounded} p-5 ${style.bg} ${style.shadow || ''} ${className}`}
            data-testid={testId}
            role="alert"
        >
            <div
                className={
                    stackOnMobile
                        ? 'flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between'
                        : 'flex items-center justify-between gap-4'
                }
            >
                <div
                    className={
                        stackOnMobile ? 'flex flex-col items-start gap-2 sm:flex-row sm:items-center' : 'flex items-center gap-2'
                    }
                >
                    {withIcon &&
                        resolvedIcon &&
                        (iconColor ? (
                            <span
                                aria-hidden
                                className={effectiveIconClassName}
                                style={{
                                    backgroundColor: iconColor,
                                    display: 'inline-block',
                                    WebkitMaskImage: `url(${resolvedIcon})`,
                                    maskImage: `url(${resolvedIcon})`,
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskRepeat: 'no-repeat',
                                    WebkitMaskSize: 'contain',
                                    maskSize: 'contain',
                                    WebkitMaskPosition: 'center',
                                    maskPosition: 'center'
                                }}
                            />
                        ) : (
                            <img className={effectiveIconClassName} alt="Alert icon" src={resolvedIcon} />
                        ))}
                    <div className="flex flex-col">
                        <span
                            data-testid={`${testId}-label`}
                            className={`${style.text} ${boldLabel ? 'font-bold' : 'font-normal'} ${biggerLabel ? 'text-lg' : 'text-base'} ${style.labelClassName || ''} ${labelClassName}`}
                        >
                            {label}
                        </span>
                        {description && (
                            <span
                                data-testid={`${testId}-description`}
                                className={`${style.text} font-normal ${style.descriptionClassName || ''} ${descriptionClassName}`}
                            >
                                {description}
                            </span>
                        )}
                    </div>
                </div>
                {action && (
                    <div className="shrink-0" data-testid={`${testId}-action`}>
                        {action}
                    </div>
                )}
            </div>
            {children && (
                <div data-testid={`${testId}-children`} className={(childrenClassName || style.children || '') + ''}>
                    {children}
                </div>
            )}
        </div>
    );
};

export default Alert;
